'use client';

import { Container, Typography, ListItem, Box } from '@mui/material';
import { spacing, color, breakpoints, lineHeight } from '@/theme/tokens';
import { usePathname } from 'next/navigation';
import TitleBar from '../TitleBar';
import { useTranslations } from 'next-intl';
import Sidebar from './Sidebar';
import SidebarStyledList from './SidebarStyledList';
import SidebarStyledLink from './SidebarStyledLink';
import styled from '@emotion/styled';

type MenuItem = {
    id: string;
    label: string;
    href: string;
    icon?: React.ReactNode;
    hideOnMobile?: boolean;
};

// Non-interactive group label rendered between links (e.g. the "Developer" section).
type MenuHeader = {
    type: 'header';
    id: string;
    label: string;
};

export type SidebarMenuItem = MenuItem | MenuHeader;

const isMenuHeader = (item: SidebarMenuItem): item is MenuHeader => 'type' in item && item.type === 'header';

type Props = {
    menuItems: SidebarMenuItem[];
    title: string;
    children: React.ReactNode;
    activeId?: string;
};

const SidebarContainer = styled(Container)`
    display: flex;
    gap: ${spacing[48]};
    flex-direction: column;

    @media (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        gap: ${spacing[24]};
    }

    @media (min-width: ${breakpoints.md}) {
        gap: ${spacing[32]};
    }
`;

const MainContent = styled(Box)`
    flex: 1;
    min-width: 0;
    margin-bottom: ${spacing[24]};

    @media (min-width: ${breakpoints.sm}) {
        margin-bottom: ${spacing[32]};
    }
    @media (min-width: ${breakpoints.md}) {
        margin-bottom: ${spacing[40]};
    }
`;

export default function BaseSidebarLayout({ menuItems, title, children, activeId }: Props) {
    const pathname = usePathname();

    return (
        <>
            <Container>
                <Typography
                    variant="heading3bold"
                    component="h1"
                    color={color.gray[80]}
                    sx={{
                        paddingY: { xs: spacing[24], sm: spacing[32], md: spacing[40] },
                        lineHeight: lineHeight[120],
                        height: '46px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        alignSelf: 'stretch',
                    }}
                >
                    {title}
                </Typography>
            </Container>
            <SidebarContainer>
                <Sidebar>
                    <SidebarStyledList>
                        {menuItems.map(item => {
                            if (isMenuHeader(item)) {
                                return (
                                    <ListItem key={item.id} disablePadding>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                borderTop: `1px solid ${color.gray[30]}`,
                                                // 4px list gap + 8px margin above the divider, 12px to the
                                                // label, 8px + 4px gap below it — a uniform 12px rhythm.
                                                marginTop: spacing[8],
                                                paddingTop: spacing[12],
                                                paddingBottom: spacing[8],
                                                paddingX: spacing[12],
                                            }}
                                        >
                                            <Typography variant="label5medium" color={color.gray[60]}>
                                                {item.label}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                );
                            }
                            const isActive = activeId
                                ? item.id === activeId
                                : pathname === item.href ||
                                  (item.href.includes('?') && pathname === item.href.split('?')[0]);
                            return (
                                <ListItem
                                    key={item.id}
                                    disablePadding
                                    sx={item.hideOnMobile ? { display: { xs: 'none', sm: 'flex' } } : undefined}
                                >
                                    <SidebarStyledLink href={item.href} isActive={isActive}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                            }}
                                        >
                                            <Typography variant="label5medium" color={color.gray[80]}>
                                                {item.label}
                                            </Typography>
                                            {item.icon}
                                        </Box>
                                    </SidebarStyledLink>
                                </ListItem>
                            );
                        })}
                    </SidebarStyledList>
                </Sidebar>
                <MainContent>{children}</MainContent>
            </SidebarContainer>
        </>
    );
}
